import { LicenseStatus, OaiboxStatus, Tenant, TenantId } from '@allbesmart/web-app-common';
import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { merge, Subscription, tap } from 'rxjs';
import { TenantManagementService } from "../../services/tenant-management.service";
import { AvailableRoutes } from "../../enums/available-routes";
import { UserRole } from '@allbesmart/web-app-keycloak-auth';
import { OaiboxTelemetryService } from "../../services/oaibox-telemetry.service";
import Keycloak from "keycloak-js";
import { BuildService } from "../../services/build.service";
import { AiRanTelemetryService } from 'src/app/services/ai-ran-telemetry.service';
import { isAiRanType } from "../../helpers/oaibox-type";
import { CdkVirtualScrollableElement, CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { ClientRowComponent } from '../../components/client-row/client-row.component';
import { DeploymentsKpisComponent } from '../../components/deployments-kpis/deployments-kpis.component';

@Component({
    selector: 'oaibox-clients-list-page',
    templateUrl: './clients-list-page.component.html',
    styleUrls: ['./clients-list-page.component.css'],
    imports: [
        CdkVirtualScrollableElement,
        ReactiveFormsModule,
        FormsModule,
        NgClass,
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        ClientRowComponent,
        DeploymentsKpisComponent,
    ],
})
export class ClientsListPageComponent implements OnDestroy {

  protected readonly isRoot: boolean;

  protected readonly AVAILABLE_LICENSE_STATUS = Object.values(LicenseStatus);
  private LICENSE_STATUS_FILTER_LOCAL_STORAGE_ITEM_KEY = '537cd297-8ac0-407e-838e-8bca0043c71a';
  private selectedLicenseStatus?: LicenseStatus;

  private TENANTS_FILTER_LOCAL_STORAGE_ITEM_KEY = '3b8cf7fb-55fb-4b4d-aae9-1492a87f61d9';
  private selectedFilterOption: TenantsFilterOption;

  private tenants: Map<TenantId, Tenant> = new Map();
  protected shownTenants: Tenant[] = [];

  protected keyword: string = '';

  private oaiboxStatusSubscription: Subscription;

  constructor(
    keycloak: Keycloak,
    tenantManagementService: TenantManagementService,
    buildService: BuildService,
    private router: Router,
    private oaiboxTelemetryService: OaiboxTelemetryService,
    private aiRanTelemetryService: AiRanTelemetryService,
  ) {
    this.isRoot = keycloak.hasRealmRole(UserRole.ROOT);

    const licenseStatusOption = window.localStorage.getItem(this.LICENSE_STATUS_FILTER_LOCAL_STORAGE_ITEM_KEY);
    this.selectedLicenseStatus = (licenseStatusOption as LicenseStatus) || undefined;

    const filterOption = window.localStorage.getItem(this.TENANTS_FILTER_LOCAL_STORAGE_ITEM_KEY);
    this.selectedFilterOption = (filterOption as TenantsFilterOption) || 'online';

    this.oaiboxStatusSubscription = merge(
      this.oaiboxTelemetryService.getOaiboxStatusChanges$(),
      this.aiRanTelemetryService.getOaiboxStatusChanges$(),
      tenantManagementService.getTenants$()
    )
    .subscribe(() => {
      this.handleTenantsChanges(tenantManagementService.getTenants());
    });

    tenantManagementService.refreshTenants();

    if (keycloak.hasRealmRole(UserRole.ROOT)) {
      buildService.fetchAllBuildDetails();
    } else if (keycloak.hasRealmRole(UserRole.RESELLER)) {
      buildService.fetchAvailableBuildCommit();
    }
  }

  private handleTenantsChanges(tenants: Tenant[]) {
    tenants.forEach(tenant => {
      if (!this.tenants.has(tenant.id)) {
        this.tenants.set(tenant.id, tenant);
      }
    });

    // Remove missing tenants
    this.tenants.forEach((tenant, tenantId) => {
      if (!tenants.includes(tenant)) {
        this.tenants.delete(tenantId);
      }
    });

    // Sort and filter tenants
    this.filterMatchingTenants();
  }

  private isTenantOnline(tenant: Tenant): boolean {
    const result = tenant.registeredMachines.some(machine => {
      if (isAiRanType(machine.productDetails.oaiboxType)) {
        return this.aiRanTelemetryService.getAiRanTelemetry(machine.id)?.status === OaiboxStatus.ONLINE;
      }
      return this.oaiboxTelemetryService.getOaiboxTelemetry(machine.id)?.status === OaiboxStatus.ONLINE;
    });
    return result;
  }

  public filterMatchingTenants(): void {
     let filteredTenants = [...this.tenants.values()];

    if (this.selectedLicenseStatus) {
      filteredTenants = filteredTenants.filter(tenant => tenant.license?.status === this.selectedLicenseStatus);
    }

    if (this.selectedFilterOption === 'online') {
      filteredTenants = filteredTenants.filter(tenant => this.isTenantOnline(tenant));
    } else if (this.selectedFilterOption === 'offline') {
      filteredTenants = filteredTenants.filter(tenant => !this.isTenantOnline(tenant));
    }

    this.shownTenants = filteredTenants;

    if (this.keyword) {
      this.shownTenants = this.shownTenants.filter(tenant => {
        const kw = this.keyword.toLowerCase();
        return tenant.clientName.toLowerCase().includes(kw) ||
          tenant.clientDescription?.toLowerCase().includes(kw) ||
          tenant.id?.toLowerCase().includes(kw) ||
          tenant.clientDomain.toLowerCase().includes(kw) ||
          tenant.registeredMachines.some(m => m.oaiboxSerialNumber?.toLowerCase().includes(kw));
      });
    }
    this.sortTenants();
  }

  private sortTenants(): void {
    const sortFn = (tenant1: Tenant, tenant2: Tenant) => {
      if (tenant1.clientName > tenant2.clientName) return 1;
      else if (tenant1.clientName < tenant2.clientName) return -1;
      else
        // If tenant names are the same, then sort DESC by number of machines
        return tenant2.registeredMachines.length - tenant1.registeredMachines.length;
    }
    this.shownTenants.sort(sortFn);
  }

  protected goToNewDeploymentPage(): void {
    this.router.navigate([AvailableRoutes.ADMIN, AvailableRoutes.NEW_DEPLOYMENT]);
  }

  get licenseStatusOption(): LicenseStatus | undefined {
    return this.selectedLicenseStatus;
  }

  set licenseStatusOption(status: LicenseStatus | undefined) {
    this.selectedLicenseStatus = status;
    if (status) window.localStorage.setItem(this.LICENSE_STATUS_FILTER_LOCAL_STORAGE_ITEM_KEY, status);
    else window.localStorage.removeItem(this.LICENSE_STATUS_FILTER_LOCAL_STORAGE_ITEM_KEY);

    this.filterMatchingTenants();
  }

  get tenantsFilterOption(): TenantsFilterOption {
    return this.selectedFilterOption;
  }

  set tenantsFilterOption(filterOption: TenantsFilterOption) {
    this.selectedFilterOption = filterOption;
    window.localStorage.setItem(this.TENANTS_FILTER_LOCAL_STORAGE_ITEM_KEY, filterOption);
    this.filterMatchingTenants();
  }

  ngOnDestroy(): void {
    this.oaiboxStatusSubscription.unsubscribe();
  }

}

type TenantsFilterOption = 'online' | 'offline' | 'all';
